import type { LoadingStatus } from "../ViewJobLoadingState";

import { getJobDataInput } from "./getJobDataInput";
import { useJobDataResultQuery } from "../__generated__/jobDataResult.gqlgenerated";

import { useMemo } from "react";

type UseJobDataResultProps = {
  jobKey: string | null | undefined;
  url: string | null;
};

/**
 * Because federating the JobDataResult entity as the return value
 * of the viewJob query caused the federation router to issue multiple entity queries,
 * which resulted in duplicate UIL events,
 * we've decided, for now, to fetch JobDataResult (including the matchComparisonResult field)
 * directly via JRS's jobData query.
 *
 * In the future, the Job entity may include matchComparisonResult.
 * If that happens, we should mostly revert the changes in WENG-1849
 * and go back to retrieving matchComparisonResult through the viewJob query.
 *
 * Note: This hook now runs in PARALLEL with the viewjob query (not after it).
 * applyLink is fetched separately and asynchronously via useApplyLink.
 */
export const useJobDataResult = (props: UseJobDataResultProps) => {
  const { jobKey, url } = props;

  const jobDataInput = useMemo(() => getJobDataInput(url ?? "", jobKey ?? ""), [url, jobKey]);

  const { data, loading, error, refetch } = useJobDataResultQuery({
    variables: {
      jobDataInput,
    },
    fetchPolicy: "no-cache",
  });

  const queryStatus = (): LoadingStatus => {
    if (loading) return "active";
    if (error) return "error";
    if (data) return "success";
    return "idle";
  };

  return {
    jobDataResult: data,
    jobDataResultLoading: loading,
    queryStatus: queryStatus(),
    refetchJobDataResult: refetch,
  };
};
